import {
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  useDisclosure,
  useToast
} from '@chakra-ui/react';
import { useForm } from '@mantine/form';
import Button from 'components/atoms/Button/Button';
import AuthContext from 'context/AuthProvider';
import { subscribeBackInStock } from 'helpers/customerio'; // Update import to the new Customer.io helper
import useSegment from 'hooks/useSegment';
import React, { useContext } from 'react';
import { useEffect } from 'react';

const BackInStock = ({ className, product, variant }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();
  const { track } = useSegment();

  const auth = useContext(AuthContext);
  const isLoggedIn = auth && auth.state.isLoggedIn;
  const form = useForm({
    initialValues: {
      email: '',
      loading: false
    }
  });

  useEffect(() => {
    if (isLoggedIn) {
      form.setFieldValue('email', auth.state.email);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn, auth.state.email]);

  const railwaySubscribe = async e => {
    e.preventDefault();
    const thisData = {
      email: form.values.email,
      sku: product.sku,
      variantId: `${variant}` || `${product.base_variant_id}`
    };
    form.setFieldValue('loading', true);
    try {
      const apiUrl = process.env.GATSBY_BC_API;
      // const apiUrl = `http://localhost:3000`;
      const response = await fetch(`${apiUrl}/gatsby/product/outOfStock`, {
        method: 'POST',
        body: JSON.stringify(thisData),
        headers: {
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) {
        throw new Error('Failed to fetch prescriptions');
      }
      const textResponse = await response.text();
      const data = await JSON.parse(textResponse);
      if (!data.success) {
        toast({
          position: 'top-right',
          colorScheme: 'secondary',
          title: 'Something went wrong',
          description: data.message,
          status: 'error'
        });
        return;
      }
      toast({
        position: 'top-right',
        title: 'Subscribed',
        colorScheme: 'primary',
        description: 'We will notify you when this product is back in stock.'
      });
      form.reset();
      onClose();
    } catch (error) {
      console.error(error);
      toast({
        position: 'top-right',
        title: 'Something went wrong',
        colorScheme: 'secondary',
        description: 'Please try again later.',
        status: 'error'
      });
    } finally {
      form.setFieldValue('loading', false);
    }
  };

  const customerIoSubscribe = async e => {
    const thisData = {
      email: form.values.email,
      sku: product.sku,
      sku_variant: `${variant}` || `${product.base_variant_id}`
    };

    form.setFieldValue('loading', true);
    subscribeBackInStock(thisData)
      .then(response => {
        if (response && response.status === 0) {
          toast({
            position: 'top-right',
            title: 'Subscribed',
            colorScheme: 'primary',
            description: 'We will notify you when this product is back in stock.'
          });

          track('Subscribed to Back In Stock', {
            email: form.values.email
          });

          form.reset();
          onClose();
        } else {
          // setMessage('Something went wrong, please try again later.');
          toast({
            position: 'top-right',
            colorScheme: 'secondary',
            title: 'Something went wrong',
            description: 'Please try again later.',
            status: 'error'
          });
        }
      })
      .catch(() => {
        toast({
          position: 'top-right',
          title: 'Something went wrong',
          colorScheme: 'secondary',
          description: 'Please try again later.',
          status: 'error'
        });
      })
      .finally(() => {
        form.setFieldValue('loading', false);
      });
  };

  const subscribeHandler = async e => {
    e.preventDefault();
    if (process.env.GATSBY_BACK_IN_STOCK === 'true') {
      await railwaySubscribe(e);
    } else {
      await customerIoSubscribe(e);
    }
  };

  return (
    <>
      <div className={className}>
        <p>This product is out of stock. </p>
        <Button onClick={onOpen} type={'button'} level={'tertiary'}>
          Notify Me
        </Button>
      </div>
      <Modal isOpen={isOpen} onClose={onClose} isCentered>
        <ModalOverlay />
        <ModalContent className="tester" overflow={'hidden'} maxW={'500px'} mx={{ base: 2, lg: 0 }}>
          <ModalHeader>This product is out of stock</ModalHeader>
          <form onSubmit={subscribeHandler}>
            <ModalBody>
              <Stack spacing={2}>
                <p>
                  Enter your email address below to be notified when this product is back in stock.
                </p>
                <Input
                  {...form.getInputProps('email')}
                  type="email"
                  required
                  placeholder="Email address"
                />
              </Stack>
            </ModalBody>
            <ModalFooter>
              <Button
                loading={form.values.loading}
                disabled={form.values.loading}
                type={'buttonSubmit'}
                level={'tertiary'}
              >
                Notify Me
              </Button>
            </ModalFooter>
          </form>
          <ModalCloseButton />
        </ModalContent>
      </Modal>
    </>
  );
};

export default BackInStock;
